<script setup>
import { computed, ref } from 'vue';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// The preferences a session inbox is created with, rendered from the provider's catalog
// entry: switches with a sane default, behind the advanced toggle, and the numbers that
// only mean something while their switch is on. Shared by the external instance form and
// the card of a provider registered in the Super Admin, which ask for the same things.
const props = defineProps({
  fields: { type: Array, required: true },
});

const values = defineModel({ type: Object, required: true });

// A number that belongs to a switch is asked for only while that switch is on.
const DEPENDS_ON = { history_sync_days: 'history_sync' };

const PREFERENCE_TYPES = ['boolean', 'integer'];

const showAdvancedOptions = ref(false);
const preferenceFields = computed(() =>
  props.fields.filter(field => PREFERENCE_TYPES.includes(field.type))
);

const isVisible = field => {
  const parent = DEPENDS_ON[field.name];
  return !parent || Boolean(values.value[parent]);
};

const fieldKey = field =>
  `INBOX_MGMT.ADD.WHATSAPP.SESSION.FIELDS.${field.name.toUpperCase()}`;
</script>

<template>
  <div
    v-if="!showAdvancedOptions && preferenceFields.length"
    class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%] mb-4"
  >
    <NextButton
      type="button"
      icon="i-lucide-plus"
      sm
      link
      @click="showAdvancedOptions = true"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.ADVANCED_OPTIONS') }}
    </NextButton>
  </div>
  <template v-else-if="preferenceFields.length">
    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <span class="text-sm text-gray-600">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.ADVANCED_OPTIONS') }}
      </span>
    </div>
    <template v-for="field in preferenceFields" :key="field.name">
      <div
        v-if="isVisible(field)"
        class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]"
      >
        <label v-if="field.type === 'boolean'">
          <div class="flex mb-2 items-center">
            <span class="mr-2 text-sm">
              {{ $t(`${fieldKey(field)}.LABEL`) }}
            </span>
            <Switch :id="field.name" v-model="values[field.name]" />
          </div>
        </label>
        <label v-else>
          {{ $t(`${fieldKey(field)}.LABEL`) }}
          <input
            :id="field.name"
            v-model.number="values[field.name]"
            type="number"
            min="1"
            step="1"
          />
          <span class="text-xs text-n-slate-11">
            {{ $t(`${fieldKey(field)}.DESCRIPTION`) }}
          </span>
        </label>
      </div>
    </template>
  </template>
</template>
